// CASINO.EXE — the lobby, and the shell every cabinet renders inside.
//
// The casino is the second engine on the same machine the coin already runs. Trading fees pay
// $INTERNET holders in MSFT; so does the casino. The difference is only where the fee comes from.
//
// It looks the way it would have in 2001: a Windows XP program whose client area is a casino
// website of the era. Gold serif over green felt, a marquee, 88x31 badges, an LED cashier, and
// an address bar that says where you are. Nothing about the maths or the disclosure changed.
//
// Nothing here is deployed until casino/tools/deploy.ts fills in the addresses. Until then the
// lobby renders dark and every cabinet refuses to take a bet — the same gate the coin shipped
// behind before its own launch. Practice mode (lib/casino.js PRACTICE) is the one exception:
// play money, the same arithmetic, nothing on chain, and the lobby says so at the top.
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { isMobileUA, metamaskDeepLink } from 'lib/eth';

import { ADDRESSES, CABINETS, CHIP } from 'lib/casino';
import { EXPLORER } from 'lib/eth';
import sfx from 'lib/sfx';
import mineIcon from 'assets/minesweeper/mine-icon.png';
import modemIcon from 'assets/windowsIcons/309(32x32).png';
import earthIcon from 'assets/windowsIcons/earth.png';
import BlueScreen from './BlueScreen';
import CoinFlip from './CoinFlip';
import DialUp from './DialUp';
import Dice from './Dice';
import Mines from './Mines';
import Roulette from './Roulette';
import Slots from './Slots';
import Wheel from './Wheel';
import { useCasino } from './state';
import { Badge, Blink, Chrome, FONT, GroupBox, Infobar, Led, Marquee, Plaque, T, XpButton, fmtChip, fmtChipAuto } from './ui';

const CABINET_VIEWS = {
  blueScreen: BlueScreen,
  mines: Mines,
  dialUp: DialUp,
  coinFlip: CoinFlip,
  roulette: Roulette,
  dice: Dice,
  wheel: Wheel,
  slots: Slots,
};
// The order the floor is walked in, which is the order CABINETS lists them.
const TABLE_NO = CABINETS.reduce((map, c, i) => Object.assign(map, { [c.key]: i + 1 }), {});
const TABLE_COUNT = CABINETS.length;

const short = a => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '');

/** The integer chips as LED digits, the cents as small print. */
function LedCredits({ value }) {
  const s = fmtChip(value, 2);
  if (s === '—') return <Led text="----" />;
  const [whole, cents] = s.split('.');
  return (
    <span className="cs__ledline">
      <Led text={whole.padStart(4, '0')} />
      <span className="cs__cents">.{cents} {CHIP.symbol}</span>
    </span>
  );
}

function Icon({ cabinet }) {
  if (cabinet === 'blueScreen') return <span className="cs__ico cs__ico--bsod" aria-hidden="true" />;
  if (cabinet === 'mines') return <img className="cs__ico" src={mineIcon} alt="" />;
  if (cabinet === 'dialUp') return <img className="cs__ico" src={modemIcon} alt="" />;
  if (cabinet === 'roulette') return <span className="cs__ico cs__ico--roulette" aria-hidden="true" />;
  if (cabinet === 'dice')
    return (
      <span className="cs__ico cs__ico--dice" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
    );
  if (cabinet === 'wheel') return <span className="cs__ico cs__ico--wheel" aria-hidden="true" />;
  if (cabinet === 'slots') return <img className="cs__ico" src={earthIcon} alt="" />;
  return <span className="cs__ico cs__ico--coin">H</span>;
}

export default function Casino({ onClose }) {
  const casino = useCasino();
  const [open, setOpen] = useState(null);
  const [about, setAbout] = useState(false);
  const Cabinet = open ? CABINET_VIEWS[open] : null;
  const meta = open ? CABINETS.find(c => c.key === open) : null;

  // The doors opening: once per CASINO.exe, never once per render. StrictMode runs mount
  // effects twice in development and the ref is what survives that, with sfx.casinoOpen()
  // holding its own 1.5s guard underneath as the second line of defence.
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    sfx.casinoOpen();
  }, []);

  return (
    <Chrome
      casino={casino}
      view={open}
      cabinets={CABINETS}
      onLobby={() => setOpen(null)}
      onOpenCabinet={setOpen}
      onExit={onClose}
      onAbout={() => setAbout(true)}
    >
      <Page>
        {casino.practice ? (
          <Infobar kind="info" className="cs__practice">
            <b>PRACTICE MODE.</b> Play money, no wallet, nothing on chain. Every cabinet runs the exact
            arithmetic its contract uses, against the public drand beacon, in this browser. The real
            floor is not open yet, and any contract address claiming to be the Internet Casino before
            this window says otherwise is fake.
          </Infobar>
        ) : !casino.live ? (
          <Infobar kind="warn" className="cs__dark">
            <b>The floor is not open yet.</b> The cabinets are built and tested but not deployed, so
            nothing here can take a bet. Any contract address claiming to be the Internet Casino
            before this window says otherwise is fake.
          </Infobar>
        ) : null}

        {casino.error ? (
          <Infobar kind="error" className="cs__error">
            {casino.error}
          </Infobar>
        ) : null}

        {about ? (
          <div className="cs__about">
            <div className="cs__about__box">
              <div className="cs__about__title">About Internet Casino</div>
              <p>
                <b>Internet Casino</b> version 1.0 (build 2001)
                <br />
                Eight tables. One public beacon. No server.
                <br />
                Chips: tokenized {CHIP.symbol}. Fee: 5%, fee-inclusive.
                <br />
                <span className="cs__about__dim">© the internet. Best viewed at 800×600.</span>
              </p>
              <XpButton type="button" onClick={() => setAbout(false)}>
                OK
              </XpButton>
            </div>
          </div>
        ) : null}

        {open ? (
          <>
            <Plaque title={meta.name} sub={`TABLE ${TABLE_NO[open]} OF ${TABLE_COUNT} · ${meta.tag.toUpperCase()}`}>
              <button type="button" className="cs__back" onClick={() => setOpen(null)}>
                ‹ LOBBY
              </button>
            </Plaque>
            <div className="cs__cabinet">
              <Cabinet casino={casino} />
            </div>
          </>
        ) : (
          <>
            <div className="cs__masthead">
              <div className="cs__logo">
                <div className="cs__logo__pre">★ WELCOME TO THE ★</div>
                <h1 className="cs__title">INTERNET CASINO</h1>
                <div className="cs__logo__post">EST. 2001 · EIGHT TABLES · ONE PUBLIC BEACON · NO SERVER</div>
              </div>
              <div className="cs__sticker">
                <Blink>NEW!!</Blink>
              </div>
              <div className="cs__burst">
                <span>
                  {casino.practice ? '1,000' : casino.live ? 'REAL' : 'SOON'}
                  <small>{casino.practice ? 'FREE CHIPS' : casino.live ? 'MSFT CHIPS' : 'ON THE FLOOR'}</small>
                </span>
              </div>
            </div>

            <Marquee>
              ★ WELCOME TO THE INTERNET CASINO ★ EIGHT TABLES OPEN 24/7 ★ FAIR PLAY: EVERY OUTCOME COMES FROM DRAND QUICKNET, A PUBLIC RANDOMNESS BEACON WE DO NOT RUN ★ CHIPS ARE TOKENIZED MSFT ★ 18+ ★ BEST VIEWED IN INTERNET EXPLORER AT 800×600 ★ NO DOWNLOAD REQUIRED ★
            </Marquee>

            <div className="cs__pitch">
              Eight tables, one public randomness beacon, no server. Every 5% fee funds building
              this thing; the holder-dividend switch is built into the House on chain and turns on
              later, feeding the same distributor that already pays $INTERNET holders in real MSFT.
            </div>

            <div className="cs__grid">
              {CABINETS.map(c => (
                <button key={c.key} type="button" className="cs__card" onClick={() => setOpen(c.key)}>
                  <div className="cs__card__name">{c.name}</div>
                  <div className="cs__card__top">
                    <Icon cabinet={c.key} />
                    <div>
                      <div className="cs__card__no">TABLE {TABLE_NO[c.key]} OF {TABLE_COUNT} · {c.tag.toUpperCase()}</div>
                      <div className="cs__card__line">{c.line}</div>
                    </div>
                  </div>
                  <div className={`cs__card__edge${c.fair ? ' fair' : ''}`}>{c.edge}</div>
                  <span className="cs__card__play">PLAY NOW!</span>
                </button>
              ))}
            </div>

            <div className="cs__twocol">
              <div className="cs__cashier">
                <div className="cs__cashier__title">CASHIER</div>
                <div className="cs__cashier__led">
                  <LedCredits value={casino.balance} />
                </div>
                {casino.practice ? (
                  <div className="cs__cashier__row">
                    <span className="cs__mode">PLAY MONEY</span>
                    <XpButton type="button" className="cs__reset" onClick={casino.resetPractice}>
                      RESET
                    </XpButton>
                  </div>
                ) : casino.account ? (
                  <div className="cs__cashier__row">
                    <a href={`${EXPLORER}/address/${casino.account}`} target="_blank" rel="noreferrer noopener" className="cs__addr">
                      {short(casino.account)}
                    </a>
                    <XpButton type="button" className="cs__switch" onClick={casino.switchWallet}>
                      SWITCH
                    </XpButton>
                  </div>
                ) : casino.choices.length ? (
                  <div className="cs__picker">
                    <div className="cs__picker__head">CHOOSE A WALLET</div>
                    {casino.choices.map(w => (
                      <XpButton
                        key={w.uuid}
                        type="button"
                        className="cs__picker__item"
                        onClick={() => casino.connect(w.rdns || w.uuid)}
                      >
                        {w.icon ? <img src={w.icon} alt="" className="cs__picker__icon" /> : null}
                        <span>{w.name}</span>
                      </XpButton>
                    ))}
                    <XpButton type="button" className="cs__picker__cancel" onClick={casino.cancelChoice}>
                      CANCEL
                    </XpButton>
                  </div>
                ) : !casino.hasWallet && isMobileUA() ? (
                  <div className="cs__cashier__row cs__cashier__row--stack">
                    <a href={metamaskDeepLink('/?app=casino')} className="cs__connect cs__connect--mm">
                      OPEN IN METAMASK
                    </a>
                    <div className="cs__cashier__hint">
                      Phones have no wallet in the browser. This reopens the casino inside the
                      MetaMask app, where your wallet is.
                    </div>
                  </div>
                ) : (
                  <div className="cs__cashier__row">
                    <XpButton type="button" className="cs__connect" onClick={() => casino.connect()} disabled={!casino.hasWallet}>
                      {casino.hasWallet ? 'CONNECT WALLET' : 'NO WALLET FOUND'}
                    </XpButton>
                  </div>
                )}
                <div className="cs__cashier__note">
                  {casino.practice
                    ? 'Play money lives in this browser. RESET puts 1,000 chips back.'
                    : 'Chips are tokenized MSFT. One approval covers every table.'}
                </div>
              </div>

              <GroupBox legend={casino.practice ? 'The house · practice' : 'The house'} className="cs__house">
                <div className="cs__house__rows">
                  <div>
                    <span className="k">Bankroll</span>
                    <span className="v">
                      {fmtChipAuto(casino.house && casino.house.bankroll)} {CHIP.symbol}
                    </span>
                  </div>
                  <div>
                    <span className="k">Max payout / bet</span>
                    <span className="v">
                      {fmtChipAuto(casino.house && casino.house.maxPayout)} {CHIP.symbol}
                    </span>
                  </div>
                  <div>
                    <span className="k">Chip</span>
                    <span className="v">tokenized MSFT</span>
                  </div>
                </div>
                <div className="cs__house__note">
                  {casino.practice
                    ? 'A practice house of 100,000 play chips with the same 2% exposure rule the real one runs, so a play the bankroll could not cover is refused at the door here too. On the live floor chips are the tokenized Microsoft stock the coin already pays out.'
                    : 'Chips are the same tokenized Microsoft stock the coin already pays out. Stake, payout and bankroll are all MSFT, so the house is solvent at any price and no cash-out ever reads a price feed. A bet the bankroll cannot cover is refused when you place it, never owed to you afterwards.'}
                </div>
                {casino.live ? (
                  <div className="cs__house__addr">
                    house{' '}
                    <a href={`${EXPLORER}/address/${ADDRESSES.house}`} target="_blank" rel="noreferrer noopener">
                      {short(ADDRESSES.house)}
                    </a>
                    {' · '}beacon{' '}
                    <a href={`${EXPLORER}/address/${ADDRESSES.beacon}`} target="_blank" rel="noreferrer noopener">
                      {short(ADDRESSES.beacon)}
                    </a>
                  </div>
                ) : null}
              </GroupBox>
            </div>

            <div className="cs__badges">
              <Badge title="FAIR PLAY" sub="drand quicknet" />
              <Badge title="18+ ONLY" sub="games, not income" />
              <Badge title="800×600" sub="best viewed in IE" />
              <Badge title="NO DOWNLOAD" sub="plays in the browser" />
              <Badge title="SSL SECURED" sub="https://" />
              {casino.practice ? <Badge title="PRACTICE" sub="play money" on /> : null}
            </div>

            <div className="cs__counter">
              You are visitor No. <Led text="0002001" scale={0.7} /> <span className="cs__counter__dim">(the counter is stuck on 2001, like everything else here)</span>
            </div>

            <div className="cs__legal">
              {casino.practice
                ? '18+. Practice mode moves no money and settles nothing on chain; each receipt names the drand quicknet round that decided it. These are games. They are not investments, they are not a claim on anything, and they are not a way to make money.'
                : '18+. Every cabinet settles against drand quicknet, a public randomness beacon we do not run, verified on chain. These are games. They are not investments, they are not a claim on anything, and they are not a way to make money.'}
            </div>
          </>
        )}
      </Page>
    </Chrome>
  );
}

const Page = styled.div`
  min-height: 100%;
  padding-bottom: 16px;
  font-family: ${FONT.web};
  color: ${T.cream};

  .cs__about {
    position: absolute;
    inset: 0;
    z-index: 20;
    background: rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .cs__about__box {
    background: ${T.gray};
    color: #000;
    border: 1px solid #fff;
    outline: 1px solid ${T.grayDark};
    box-shadow: 3px 3px 8px rgba(0, 0, 0, 0.5);
    padding: 12px 16px 14px;
    width: 300px;
    text-align: center;
    font: 11px ${FONT.ui};
    line-height: 1.6;
  }
  .cs__about__title {
    font-weight: 700;
    margin-bottom: 6px;
  }
  .cs__about__dim {
    color: #6d6d6d;
  }

  .cs__masthead {
    position: relative;
    padding: 18px 14px 14px;
    background: radial-gradient(ellipse at 50% 0%, #17398f, ${T.navy} 55%, ${T.navyDeep} 100%);
    border-bottom: 3px double ${T.goldDeep};
    text-align: center;
    overflow: hidden;
  }
  .cs__logo__pre,
  .cs__logo__post {
    font: 9px ${FONT.web};
    letter-spacing: 3px;
    color: ${T.gold};
  }
  .cs__logo__post {
    letter-spacing: 2px;
    color: #d9c27a;
    margin-top: 6px;
  }
  .cs__title {
    margin: 2px 0 0;
    font: bold 34px ${FONT.serif};
    letter-spacing: 6px;
    color: ${T.gold};
    text-shadow: 2px 2px 0 ${T.red}, 4px 4px 0 #3b0000, 0 0 18px rgba(255, 216, 107, 0.45);
    line-height: 1.05;
  }
  .cs__sticker {
    position: absolute;
    top: 10px;
    left: 12px;
    transform: rotate(-12deg);
    font: 22px ${FONT.sticker};
    color: #fff;
    text-shadow: 2px 2px 0 ${T.red};
    letter-spacing: 1px;
  }
  .cs__burst {
    position: absolute;
    top: 8px;
    right: 10px;
    width: 76px;
    height: 76px;
    background: ${T.gold};
    clip-path: polygon(50% 0%, 61% 12%, 76% 6%, 79% 22%, 95% 27%, 88% 41%, 100% 50%, 88% 59%, 95% 73%, 79% 78%, 76% 94%, 61% 88%, 50% 100%, 39% 88%, 24% 94%, 21% 78%, 5% 73%, 12% 59%, 0% 50%, 12% 41%, 5% 27%, 21% 22%, 24% 6%, 39% 12%);
    display: flex;
    align-items: center;
    justify-content: center;
    transform: rotate(8deg);
  }
  .cs__burst span {
    font: bold 15px ${FONT.web};
    color: ${T.red};
    line-height: 1;
    text-align: center;
  }
  .cs__burst small {
    display: block;
    font-size: 7px;
    letter-spacing: 1px;
    color: #3b2a00;
    margin-top: 2px;
  }

  @media (max-width: 440px) {
    .cs__title {
      font-size: 22px;
      letter-spacing: 3px;
    }
    .cs__burst {
      display: none;
    }
    .cs__sticker {
      font-size: 16px;
    }
  }

  .cs__pitch {
    margin: 12px 14px 0;
    padding: 8px 10px;
    background: rgba(0, 0, 0, 0.28);
    border-left: 3px solid ${T.gold};
    font-size: 11px;
    line-height: 1.6;
    color: ${T.cream};
  }

  .cs__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
    gap: 10px;
    padding: 12px 14px 0;
  }
  .cs__card {
    position: relative;
    text-align: left;
    background: linear-gradient(180deg, #0f6b3b, #0a4f2b);
    border: 3px double ${T.goldDeep};
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.4), 0 3px 6px rgba(0, 0, 0, 0.5);
    padding: 10px 11px 12px;
    cursor: pointer;
    font-family: ${FONT.web};
    color: ${T.cream};
  }
  .cs__card:hover {
    border-color: ${T.gold};
    background: linear-gradient(180deg, #13804a, #0b5a31);
  }
  .cs__card:hover .cs__card__play {
    background: linear-gradient(180deg, #fff8d8 0%, #ffe38a 38%, #e6b53a 62%, #c8961b 100%);
  }
  .cs__card__top {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    margin-top: 6px;
  }
  .cs__ico {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    image-rendering: pixelated;
  }
  .cs__ico--bsod {
    display: block;
    background: #0827a8 repeating-linear-gradient(180deg, #fff 0 1px, transparent 1px 4px, #fff 4px 5px, transparent 5px 9px);
    background-size: 60% 100%;
    background-position: 3px 6px;
    background-repeat: no-repeat;
    border: 1px solid #c8c8c8;
    box-sizing: border-box;
  }
  .cs__ico--roulette {
    display: block;
    border-radius: 50%;
    box-sizing: border-box;
    border: 2px solid #ffd86b;
    background:
      radial-gradient(circle at 50% 50%, #c9a227 0 8px, transparent 8px),
      conic-gradient(
        #0b7a3a 0 22.5deg,
        #141414 22.5deg 67.5deg,
        #c8102e 67.5deg 112.5deg,
        #141414 112.5deg 157.5deg,
        #c8102e 157.5deg 202.5deg,
        #141414 202.5deg 247.5deg,
        #c8102e 247.5deg 292.5deg,
        #141414 292.5deg 337.5deg,
        #c8102e 337.5deg 360deg
      );
  }
  .cs__ico--dice {
    display: block;
    position: relative;
    border-radius: 5px;
    box-sizing: border-box;
    background: linear-gradient(160deg, #fff, #e2e2d8);
    border: 1px solid #7a7a6a;
    box-shadow: inset 0 0 0 1px #fff, 0 1px 2px rgba(0, 0, 0, 0.5);
  }
  .cs__ico--dice i {
    position: absolute;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #141414;
  }
  .cs__ico--dice i:nth-child(1) {
    left: 5px;
    top: 5px;
  }
  .cs__ico--dice i:nth-child(2) {
    left: 13px;
    top: 13px;
  }
  .cs__ico--dice i:nth-child(3) {
    left: 21px;
    top: 21px;
  }
  .cs__ico--wheel {
    display: block;
    border-radius: 50%;
    box-sizing: border-box;
    border: 2px solid #ffd86b;
    background: conic-gradient(
      #0a3d22 0 30deg,
      #ffd86b 30deg 60deg,
      #0a3d22 60deg 90deg,
      #ff8c1a 90deg 120deg,
      #0a3d22 120deg 150deg,
      #c8102e 150deg 180deg,
      #0a3d22 180deg 210deg,
      #ffd86b 210deg 240deg,
      #0a3d22 240deg 270deg,
      #7b2ff7 270deg 300deg,
      #0a3d22 300deg 330deg,
      #ff8c1a 330deg 360deg
    );
  }
  .cs__ico--coin {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 30%, #fff3c4, #ffd86b 40%, #b8860b 85%, #7a5c00);
    border: 1px solid #7a5c00;
    box-sizing: border-box;
    color: #5a4200;
    font: bold 16px ${FONT.serif};
    text-shadow: 0 1px 0 #fff2c0;
  }
  .cs__card__no {
    font-size: 8px;
    letter-spacing: 3px;
    color: #d9c27a;
  }
  .cs__card__name {
    font: bold 15px ${FONT.serif};
    letter-spacing: 2px;
    color: ${T.gold};
    text-shadow: 1px 1px 0 #3b0000;
  }
  .cs__card__line {
    font-size: 10px;
    line-height: 1.5;
    color: ${T.cream};
    margin: 3px 0 7px;
  }
  .cs__card__edge {
    font-size: 9px;
    color: #ffc2c2;
    letter-spacing: 0.5px;
  }
  .cs__card__edge.fair {
    color: #b8f5c8;
  }
  .cs__card__play {
    display: inline-block;
    margin-top: 9px;
    padding: 4px 12px;
    background: linear-gradient(180deg, #fff3c4 0%, #ffd86b 38%, #d8a72a 62%, #b8860b 100%);
    border: 2px outset #f5d87a;
    color: #3b2a00;
    font: bold 10px ${FONT.web};
    letter-spacing: 2px;
    text-shadow: 0 1px 0 #fff2c0;
  }

  .cs__twocol {
    display: grid;
    grid-template-columns: minmax(200px, 250px) 1fr;
    gap: 10px;
    padding: 12px 14px 0;
    align-items: start;
  }
  @media (max-width: 560px) {
    .cs__twocol {
      grid-template-columns: 1fr;
    }
  }
  .cs__cashier {
    background: #000;
    border: 3px double ${T.goldDeep};
    padding: 9px 10px 10px;
    text-align: center;
  }
  .cs__cashier__title {
    font: bold 11px ${FONT.serif};
    letter-spacing: 5px;
    color: ${T.gold};
    margin-bottom: 7px;
  }
  .cs__cashier__led {
    display: flex;
    justify-content: center;
  }
  .cs__ledline {
    display: inline-flex;
    align-items: flex-end;
    gap: 5px;
  }
  .cs__cents {
    color: ${T.gold};
    font: bold 11px ${FONT.mono};
    padding-bottom: 3px;
  }
  .cs__cashier__row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 9px;
  }
  .cs__mode {
    font: bold 9px ${FONT.web};
    letter-spacing: 2px;
    color: #000;
    background: ${T.gold};
    padding: 3px 6px;
  }
  .cs__addr {
    color: ${T.gold};
    font: 11px ${FONT.mono};
  }
  .cs__switch {
    font-size: 9px;
    padding: 2px 6px;
  }
  .cs__picker {
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin-top: 9px;
  }
  .cs__picker__head {
    font: bold 9px ${FONT.web};
    letter-spacing: 2px;
    color: #d9c27a;
    text-align: center;
  }
  .cs__picker__item {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 7px;
    width: 100%;
    font-size: 11px;
    padding: 4px 8px;
  }
  .cs__picker__icon {
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
  }
  .cs__picker__cancel {
    font-size: 9px;
    align-self: center;
    padding: 2px 10px;
  }
  .cs__cashier__row--stack {
    flex-direction: column;
    gap: 6px;
  }
  .cs__connect--mm {
    display: inline-block;
    padding: 9px 18px;
    font: bold 12px ${FONT.web};
    letter-spacing: 2px;
    color: #000;
    background: linear-gradient(#ffd86b, #e0a800);
    border: 2px outset #b8860b;
    text-decoration: none;
  }
  .cs__cashier__hint {
    font-size: 9px;
    color: #d9c27a;
    line-height: 1.5;
    max-width: 260px;
  }
  /* Fingers, not cursors: every control a player must hit mid-spin gets a real target. */
  @media (pointer: coarse) {
    input[type='radio'] {
      width: 20px;
      height: 20px;
    }
    .sb__chip {
      min-width: 48px;
      min-height: 48px;
    }
    .cs__connect,
    .cs__picker__item {
      min-height: 40px;
      font-size: 12px;
    }
  }
  .cs__cashier__note {
    margin-top: 8px;
    font-size: 9px;
    color: #d9c27a;
    line-height: 1.5;
  }
  .cs__house {
    margin-top: 0;
  }
  .cs__house__rows {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
  .cs__house__rows > div {
    flex: 1;
    min-width: 96px;
    background: #fff;
    border: 1px solid #7f9db9;
    box-shadow: inset 1px 1px 0 #d6dde5;
    padding: 3px 6px;
  }
  .cs__house__rows .k {
    display: block;
    font-size: 9px;
    color: #555;
  }
  .cs__house__rows .v {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #000;
    margin-top: 1px;
  }
  .cs__house__note {
    margin-top: 8px;
    font-size: 10px;
    line-height: 1.55;
    color: #222;
  }
  .cs__house__addr {
    margin-top: 6px;
    font: 9px ${FONT.mono};
    color: #555;
  }
  .cs__house__addr a {
    color: #00f;
  }

  .cs__badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    justify-content: center;
    padding: 14px 14px 0;
  }
  .cs__counter {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: 12px;
    font-size: 10px;
    color: ${T.cream};
  }
  .cs__counter__dim {
    font-size: 9px;
    color: #b8d9c4;
  }
  .cs__legal {
    margin: 12px 14px 0;
    font-size: 9px;
    line-height: 1.7;
    color: #b8d9c4;
    text-align: center;
  }
  .cs__cabinet {
    padding: 12px 12px 0;
  }
`;
